import { FilterHeaderTableServiceType } from "./filter-header-table.service.type";
import { AppType, APP_TYPE } from "../app.type";
import { Inject, Injectable } from "@angular/core";
import {
  filterAllManufacturers,
  filterManufacturersByCPNs,
  getPartFilterResult
} from "../api.service";
import { Manufacturer } from "../../../../shared/models/manufacturer";
import { FilterPage } from "../filter-list/filter-list.state";
import { StringUtils } from "../../../../shared/utils/string.utils";
import { SearchWords } from "../filter/filter.service.type";
import { BehaviorSubject } from "rxjs";

@Injectable()
export class FilterHeaderTableService implements FilterHeaderTableServiceType {
  result: any = {};
  filterByPage: FilterPage[] = [];
  filters: FilterPage[] = [];
  filterValues: SearchWords = {};
  isFilterSelected = false;
  manufacturersListSearchResult: string[] = [];
  partsListSearchResult: string[] = [];
  nameSearchResult: string[] = [];
  // tslint:disable-next-line
  MPNIds: string[] = [];
  resetAllFilters: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(
    false
  );

  constructor(@Inject(APP_TYPE) private app: AppType) {}

  getFieldId(docType: string, columnName: string) {
    return docType + "." + columnName;
  }

  async filter(field: string, value: string) {
    if (StringUtils.isNullOrEmpty(value)) {
      return;
    }
    this.app.spinner.showSpinner();
    this.filterValues = this.createFilterObjectByView(field, value);
    this.app.manufacturersFilter.checkProactivePrefilterRemoved(field);

    if (Object.keys(this.filterValues).length === 0) {
      if (this.app.type === "manufacturer") {
        if (this.app.RM.fromRMSearch) {
          await this.app.manufacturersFilter.getFilterResultBasedOnView("1");
        } else {
          await this.app.manufacturersFilter.getManufacturers(this.app.RM.mode);
        }
      } else if (this.app.type === "part") {
        this.app.partsTable.getFilterResultBasedOnView("1");
      }
      this.isFilterSelected = false;
      this.app.spinner.hideSpinner();
      this.app.paginator.resetPagination();
      return;
    }

    if (this.app.type === "part") {
      await this.getPartSearchResult();
    }

    if (this.app.type === "manufacturer") {
      await this.getManufacturerSearchResult();
      this.isFilterSelected = true;
    }

    this.app.spinner.hideSpinner();
    this.app.paginator.resetPagination();
  }

  /** Applies a date interval: the keys are the "<column>From" / "<column>To" pair of one column. */
  async filterDateRange(k: any) {
    this.app.spinner.showSpinner();
    Object.keys(k).forEach((prop) => {
      // Only one end of the interval has to be picked, and the date adapter reports a cleared
      // date as 0, so an end left empty is skipped rather than sent as a bound nothing matches.
      if (StringUtils.isNullOrEmpty(k[prop]) || k[prop] === 0) {
        return;
      }
      this.filterValues = this.createFilterObjectByView(prop, k[prop]);
    });
    if (this.app.type === "part") {
      await this.getPartSearchResult();
    }

    if (this.app.type === "manufacturer") {
      await this.getManufacturerSearchResult();
      this.isFilterSelected = true;
    }

    this.app.spinner.hideSpinner();
    this.app.paginator.resetPagination();
  }

  async clearFilter(fieldId: string) {
    const propertyName = fieldId.split(".")[1];
    delete this.filterValues[propertyName];

    this.filterByPage = this.filterByPage.filter(
      (filter) => filter.filterLabelField !== fieldId
    );
    this.filters = this.getCurrentFilters();
    this.app.manufacturersFilter.checkProactivePrefilterRemoved(fieldId);

    if (this.app.type === "manufacturer") {
      if (Object.keys(this.filterValues).length > 0) {
        await this.getManufacturerSearchResult();
      } else if (this.app.RM.fromRMSearch) {
        await this.app.manufacturersFilter.getFilterResultBasedOnView("1");
      } else {
        await this.app.manufacturersFilter.getManufacturers(this.app.RM.mode);
      }
    } else if (this.app.type === "part") {
      if (Object.keys(this.filterValues).length > 0) {
        await this.getPartSearchResult();
      } else {
        this.app.partsTable.getFilterResultBasedOnView("1");
      }
    }
    this.isFilterSelected = false;
    this.app.paginator.resetPagination();
  }

  /** Clears the date interval applied to one column, leaving the other filters in place. */
  async clearDateRangeFilter(fieldId: string) {
    const { from, to } = this.app.datesFilter.getColumnRangeFields(fieldId);

    delete this.filterValues[from.split(".")[1]];
    delete this.filterValues[to.split(".")[1]];

    this.filterByPage = this.filterByPage.filter(
      (filter) =>
        filter.filterLabelField !== from && filter.filterLabelField !== to
    );
    this.filters = this.getCurrentFilters();

    if (Object.keys(this.filterValues).length > 0) {
      await this.getManufacturerSearchResult();
    } else if (this.app.RM.fromRMSearch) {
      await this.app.manufacturersFilter.getFilterResultBasedOnView("1");
    } else {
      await this.app.manufacturersFilter.getManufacturers(this.app.RM.mode);
    }
  }

  createFilterObjectByView(fieldName: string, value: string) {
    const filter: FilterPage = {
      values: value,
      filterView: this.app.RM.mode as any,
      filterLabelField: fieldName
    };

    // Columns of type: "value", "text" & "radio" could be filtered only by one value at a time
    if (
      this.app.field.getType(fieldName) === "value" ||
      this.app.field.getType(fieldName) === "text" ||
      this.app.field.getType(fieldName) === "radio"
    ) {
      this.removeInputDupFilter(fieldName);
    }

    const index = this.filterByPage.findIndex(
      (e) => e.filterLabelField === fieldName && e.values === value
    );

    if (index === -1) {
      this.filterByPage.push(filter);
    } else {
      this.filterByPage.splice(index, 1);
    }

    this.filters = this.getCurrentFilters();
    return this.app.filter.setFilterValues(this.filters);
  }

  getCurrentFilters() {
    this.filters = this.filterByPage.filter(
      (view: any) => view.filterView === this.app.RM.mode
    );
    return this.filters;
  }

  isChecked(field: string, option: string) {
    const filters = this.filters
      .filter((f: FilterPage) => f.filterLabelField === field)
      .map((v) => v.values);

    if (filters.length === 0) {
      return false;
    }

    if (filters.findIndex((o: string) => o === option) !== -1) {
      return true;
    }
    return false;
  }

  isSelected(field: string): boolean {
    if (
      this.filters.findIndex(
        (filter: FilterPage) => filter.filterLabelField === field
      ) !== -1
    ) {
      return true;
    }
    return false;
  }

  resetHeaderFilters() {
    this.filterValues = {};
    this.filters = [];
    this.filterByPage = [];
    // RAD: the proactive prefilter is the default state of the manufacturers header filters
    this.app.manufacturersFilter.applyProactivePrefilter();
  }

  async getManufacturerSearchResult(page?: string) {
    const p = page != null ? page : "1";
    let mpnsByCPN: { manufacturerPartNumber: string; partNumber: string }[] =
      [];
    this.app.spinner.showSpinner();

    // the "Engineering" view lists all manufacturers under the assembly (same as all-manufacturers)
    const mode =
      this.app.RM.mode === "engineering"
        ? "all-manufacturers"
        : this.app.RM.mode;

    /** if the current view from RM search, try to get first all the MPNS contained inside the selected CPN  */
    if (this.app.RM.fromRMSearch) {
      const result = await filterManufacturersByCPNs(
        this.app.customers.expectCurrent,
        mode,
        this.app.tree.partsOfSearchedAssembly,
        "all",
        "all"
      );
      mpnsByCPN = result.manufacturers.map((m) => ({
        manufacturerPartNumber: m.manufacturerPartNumber,
        partNumber: m.partNumber
      }));
    } else {
      mpnsByCPN = [];
    }

    /** get the list of manufacturers based on the previous list resulted
     * the sort travels with every request so each page comes back ordered by the
     * column the table is sorted by, not just the rows already on screen */
    this.result = await filterAllManufacturers(
      this.app.customers.expectCurrent,
      this.filterValues,
      mode,
      p,
      this.app.paginator.pageSize.toString(),
      mpnsByCPN,
      this.app.table.sortField,
      this.app.table.sortDirection
    );
    this.MPNIds = this.result.mpnIds;
    this.app.manufacturersFilter.manufacturers = this.result.manufacturers;

    this.app.manufacturersFilter.pages = this.result.size;
    this.app.manufacturersFilter.currentIds = this.result.manufacturers.map(
      (manufacturer: Manufacturer) => manufacturer._id
    );
    this.app.spinner.hideSpinner();
  }

  async getPartSearchResult(page?: string) {
    const p = page != null ? page : "1";
    this.app.spinner.showSpinner();

    this.result = await getPartFilterResult(
      this.app.customers.expectCurrent,
      Object.keys(this.filterValues),
      this.filterValues,
      this.app.RM.mode,
      p,
      this.app.paginator.pageSize.toString(),
      this.app.filterTree.assemblySelected,
      this.app.table.sortField,
      this.app.table.sortDirection
    );
    this.app.partsTable.parts = this.result.parts;
    this.app.partsTable.size = this.result.size;
    this.partsListSearchResult = this.result.typeAheadCPNs;

    this.app.spinner.hideSpinner();
  }

  getFieldType(field: string) {
    const fieldType = this.app.field.getType(field);

    switch (fieldType) {
      case "date":
      case "timestamp":
        return "dateInterval";
      case "text":
      case "number":
        return "value";
      case "radio":
        // Display the radio button using dropdowns, as the options field
        return "options";
      case "checkbox":
        // Checkbox fields with Yes/No options are filtered with the same dropdown
        return this.app.field.getOptions(field).length > 0 ? "options" : fieldType;
      default:
        return fieldType;
    }
  }

  private removeInputDupFilter(fieldName: string) {
    const index = this.filterByPage.findIndex(
      (e) => e.filterLabelField === fieldName
    );

    if (index !== -1) {
      this.filterByPage.splice(index, 1);
    }
  }
}
